import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";

import DetallesOrdenCompra from "components/DetallesOrdenCompra";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import { useMaterialUIController } from "context";

// const API_URL = ... (removed)
const empresaId = localStorage.getItem("empresaId") || "1";

export default function ListaCompras() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    busqueda: "",
    fechaInicio: "",
    fechaFin: "",
    estatus: "todos",
  });

  // Estados para el modal de detalles
  const [openDetalles, setOpenDetalles] = useState(false);
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchCompras();
  }, [filtros.estatus]);

  const fetchCompras = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.COMPRAS, {
        params: {
          empresa_id: empresaId,
          ...(filtros.estatus !== "todos" && { estatus: filtros.estatus }),
        },
      });
      setCompras(response.data);
    } catch (error) {
      console.error("Error al cargar compras:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetalles = async (id) => {
    try {
      setLoadingDetails(true);
      const response = await axios.get(API_ENDPOINTS.COMPRA(id));
      setSelectedOrden(response.data);
      setOpenDetalles(true);
    } catch (error) {
      console.error("Error al obtener detalles de la orden:", error);
      alert("No se pudo cargar la información de la orden.");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetalles = () => {
    setOpenDetalles(false);
    setSelectedOrden(null);
  };

  const comprasFiltradas = compras.filter((compra) => {
    const matchBusqueda =
      compra.folio?.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
      compra.proveedor?.nombre?.toLowerCase().includes(filtros.busqueda.toLowerCase());

    return matchBusqueda;
  });

  const getEstatusColor = (estatus) => {
    switch (estatus) {
      case "recibida":
        return "success";
      case "pendiente":
        return "warning";
      case "parcial":
        return "info";
      case "cancelada":
        return "error";
      default:
        return "default";
    }
  };

  const columns = [
    { Header: "Folio", accessor: "folio", width: "12%" },
    { Header: "Proveedor", accessor: "proveedor", width: "20%" },
    { Header: "Fecha Orden", accessor: "fecha", width: "12%" },
    { Header: "Tipo", accessor: "tipo", width: "12%" },
    { Header: "Total", accessor: "total", width: "15%" },
    { Header: "Estatus", accessor: "estatus", align: "center", width: "12%" },
    { Header: "Acciones", accessor: "acciones", align: "center", width: "12%" },
  ];

  const rows = comprasFiltradas.map((compra) => ({
    folio: compra.folio,
    proveedor: compra.proveedor?.nombre || "N/A",
    fecha: new Date(compra.fecha_orden).toLocaleDateString("es-MX"),
    tipo: compra.tipo_compra || "contado",
    total: `$${parseFloat(compra.total || 0).toLocaleString("es-MX", {
      minimumFractionDigits: 2,
    })}`,
    estatus: <Chip label={compra.estatus} color={getEstatusColor(compra.estatus)} size="small" />,
    acciones: (
      <MDBox display="flex" gap={1} justifyContent="center">
        <Icon
          sx={{ cursor: "pointer", fontSize: 20 }}
          color="info"
          onClick={() => handleOpenDetalles(compra.id)}
        >
          visibility
        </Icon>
        <Icon sx={{ cursor: "pointer", fontSize: 20 }} color="primary">
          edit
        </Icon>
      </MDBox>
    ),
  }));

  return (
    <MDBox>
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <TextField
            label="Buscar"
            fullWidth
            variant="outlined"
            size="small"
            value={filtros.busqueda}
            onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
            placeholder="Folio o proveedor"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            select
            label="Estatus"
            fullWidth
            variant="outlined"
            size="small"
            value={filtros.estatus}
            onChange={(e) => setFiltros({ ...filtros, estatus: e.target.value })}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="pendiente">Pendiente</MenuItem>
            <MenuItem value="recibida">Recibida</MenuItem>
            <MenuItem value="parcial">Parcial</MenuItem>
            <MenuItem value="cancelada">Cancelada</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            type="date"
            label="Fecha Inicio"
            fullWidth
            variant="outlined"
            size="small"
            value={filtros.fechaInicio}
            onChange={(e) => setFiltros({ ...filtros, fechaInicio: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            type="date"
            label="Fecha Fin"
            fullWidth
            variant="outlined"
            size="small"
            value={filtros.fechaFin}
            onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Card>
        <MDBox p={3}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDTypography variant="h6">
              <Icon sx={{ mr: 1 }}>shopping_cart</Icon>
              Órdenes de Compra ({comprasFiltradas.length})
            </MDTypography>
            <MDButton variant="gradient" color={sidenavColor} size="small">
              <Icon sx={{ mr: 1 }}>file_download</Icon>
              Exportar
            </MDButton>
          </MDBox>
          {loading ? (
            <MDTypography variant="body2">Cargando compras...</MDTypography>
          ) : (
            <DataTable
              table={{ columns, rows }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={false}
            />
          )}
        </MDBox>
      </Card>

      <DetallesOrdenCompra
        open={openDetalles}
        onClose={handleCloseDetalles}
        orden={selectedOrden}
      />
    </MDBox>
  );
}
